import type { ExcalidrawProps } from '@xuwo/drawing/types.hostApi';

import { FONT_FAMILY, getFontString, VERTICAL_ALIGN } from '../common/index';
import { newTextElement } from './newElement';
import { wrapText } from './textWrapping';
import { isIframeElement } from './typeChecks';
import type {
  ExcalidrawElement,
  ExcalidrawIframeLikeElement,
  NonDeletedExcalidrawElement,
} from './types';

// XuWo: upstream turned links into iframes for a whitelist of sites
// (YouTube, Vimeo, Figma, Twitter, GitHub gists, …). The board embeds only
// what the host renders for itself: a link is accepted only when the host's
// `validateEmbeddable` says so, and nothing is ever loaded from the link.

export const createPlaceholderEmbeddableLabel = (
  element: ExcalidrawIframeLikeElement
): NonDeletedExcalidrawElement => {
  let text: string;
  if (isIframeElement(element)) {
    text = 'IFrame element';
  } else {
    text =
      !element.link || element?.link === '' ? 'Empty Web-Embed' : element.link;
  }

  const fontSize = Math.max(
    Math.min(element.width / 2, element.width / text.length),
    element.width / 30
  );
  const fontFamily = FONT_FAMILY.Sans;

  const fontString = getFontString({
    fontSize,
    fontFamily,
  });

  return newTextElement({
    x: element.x + element.width / 2,
    y: element.y + element.height / 2,
    strokeColor:
      element.strokeColor !== 'transparent' ? element.strokeColor : 'black',
    backgroundColor: 'transparent',
    fontFamily,
    fontSize,
    text: wrapText(text, fontString, element.width - 20),
    textAlign: 'center',
    verticalAlign: VERTICAL_ALIGN.MIDDLE,
    angle: element.angle ?? 0,
  });
};

export const embeddableURLValidator = (
  url: string | null | undefined,
  validateEmbeddable: ExcalidrawProps['validateEmbeddable']
): boolean => !!url && validateEmbeddable?.(url) === true;

/** Native canvas runs and browser-rendered elements share the document stack. */
export type DomSceneLayer<T> =
  | { kind: 'dom'; element: T }
  | { kind: 'canvas'; key: string; elements: readonly T[] };

export const getDomSceneLayers = <T extends ExcalidrawElement>(
  elements: readonly T[],
  isDom: (element: T) => boolean
): { base: readonly T[]; layers: DomSceneLayer<T>[] } => {
  const first = elements.findIndex(isDom);
  if (first === -1) {
    return { base: elements, layers: [] };
  }
  const layers: DomSceneLayer<T>[] = [];
  let native: T[] = [];
  const flush = () => {
    if (native.length) {
      layers.push({ kind: 'canvas', key: native[0].id, elements: native });
      native = [];
    }
  };
  for (const element of elements.slice(first)) {
    if (isDom(element)) {
      flush();
      layers.push({ kind: 'dom', element });
    } else {
      native.push(element);
    }
  }
  flush();
  return { base: elements.slice(0, first), layers };
};
